// File: src/App.jsx
// TehranAA Meetings - Professional PWA App

import { useState, useEffect, createContext, useContext } from 'react';

import {
  Sun,
  Moon,
  Calendar,
  List,
  Navigation,
  MapPin,
  Info,
  WifiOff,
  RefreshCw,
} from 'lucide-react';

import TodayPage from './pages/TodayPage';
import AllMeetingsPage from './pages/AllMeetingsPage';
import NearestPage from './pages/NearestPage';

import AboutPage from './pages/AboutPage';
import TagPage from './pages/TagPage';

import {
  useNotifications,
  useAlarmSync,
  NotificationSetup,
} from './hooks/useNotifications';

import {
  clearOldCaches,
  getIranNow,
  getIranDayOfWeek,
  DAYS_OF_WEEK,
  buildApiUrl,
} from './data/constants';

import {
  useMeetings,
  useTodayStatus,
} from './hooks/useMeetings';

import { getUrlParam, setUrlParams } from './hooks/useUrlParams';

/* ══════════════════════════════════════════════════
   Theme Context & Optimized Color Palette
══════════════════════════════════════════════════ */

export const ThemeCtx = createContext({
  dark: true,
  toggle: () => {},
});

export const useTheme = () => useContext(ThemeCtx);

const LIGHT = {
  '--bg': '#f8fafc',
  '--bg2': '#ffffff',
  '--bg3': '#f1f5f9',
  '--border': '#e2e8f0',
  '--border2': '#cbd5e1',
  '--text': '#0f172a',
  '--text2': '#475569',
  '--text3': '#64748b',
  '--accent': '#0284c7',
  '--accent2': '#0ea5e9',
};

const DARK = {
  '--bg': '#0f172a',
  '--bg2': '#1e293b',
  '--bg3': '#334155',
  '--border': '#334155',
  '--border2': '#475569',
  '--text': '#f8fafc',
  '--text2': '#cbd5e1',
  '--text3': '#94a3b8',
  '--accent': '#38bdf8',
  '--accent2': '#7dd3fc',
};

function applyTheme(dark) {
  const vars = dark ? DARK : LIGHT;

  Object.entries(vars).forEach(([k, v]) => {
    document.documentElement.style.setProperty(k, v);
  });

  document.documentElement.setAttribute(
    'data-theme',
    dark ? 'dark' : 'light'
  );
}

/* ══════════════════════════════════════════════════
   Tabs
══════════════════════════════════════════════════ */

const TABS = [
  { id: 'today', Icon: Calendar, label: 'امروز' },
  { id: 'all', Icon: List, label: 'همه' },
  { id: 'nearest', Icon: Navigation, label: 'نزدیک‌ترین' },

  { id: 'about', Icon: Info, label: 'درباره' },
];

const PAGE_TITLES = {
  today: 'جلسات امروز',
  all: 'همه جلسات',
  nearest: 'نزدیک‌ترین',

  about: 'درباره AA',
};

/* ══════════════════════════════════════════════════
   Header (with Optimized Dynamic Blur)
══════════════════════════════════════════════════ */

function Header({
  tab,
  isOffline,
  dark,
  toggleTheme,
  tagActive,
}) {
  const [now, setNow] = useState(getIranNow());

  useEffect(() => {
    const t = setInterval(() => {
      setNow(getIranNow());
    }, 30000);

    return () => clearInterval(t);
  }, []);

  const dow = getIranDayOfWeek(now);

  const dayName =
    DAYS_OF_WEEK.find((d) => d.id === dow)?.name || '';

  const time = now.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <header
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid var(--border)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: dark
          ? 'rgba(15, 23, 42, 0.85)'
          : 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background:
              'linear-gradient(135deg,#0369a1,#0ea5e9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          🔵
        </div>

        <div>
          <div
            style={{
              fontSize: 8,
              fontWeight: 900,
              color: 'var(--accent2)',
            }}
          >
            TehranAA
          </div>

          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              color: 'var(--text)',
            }}
          >
            {tagActive ? 'برچسب' : PAGE_TITLES[tab]}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {isOffline && (
          <WifiOff size={15} color="#fbbf24" />
        )}

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 900,
              color: 'var(--text)',
              direction: 'ltr',
            }}
          >
            {time}
          </div>

          <div
            style={{
              fontSize: 9,
              color: 'var(--text3)',
            }}
          >
            {dayName}
          </div>
        </div>

        <button
          onClick={toggleTheme}
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            border: `1px solid var(--border)`,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {dark ? (
            <Sun size={16} color="#fbbf24" />
          ) : (
            <Moon size={16} color="var(--text2)" />
          )}
        </button>
      </div>
    </header>
  );
}

/* ══════════════════════════════════════════════════
   Bottom Navigation
══════════════════════════════════════════════════ */

function BottomNav({ tab, setTab }) {
  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'var(--bg)',
        borderTop: `1px solid var(--border)`,
        paddingBottom:
          'max(6px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        style={{
          display: 'flex',
          maxWidth: 560,
          margin: '0 auto',
        }}
      >
        {TABS.map(({ id, Icon, label }) => {
          const active = tab === id;

          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '9px 4px 5px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: active
                  ? 'var(--accent2)'
                  : 'var(--text3)',
              }}
            >
              <Icon
                size={20}
                color={
                  active
                    ? 'var(--accent2)'
                    : 'var(--text3)'
                }
              />

              <span
                style={{
                  fontSize: 9,
                  fontWeight: active ? 900 : 500,
                }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ══════════════════════════════════════════════════
   Install Banner
══════════════════════════════════════════════════ */

function InstallBanner({ prompt, onDismiss }) {
  if (!prompt) return null;

  const installApp = async () => {
    try {
      await prompt.prompt();

      const choice = await prompt.userChoice;

      if (choice.outcome === 'accepted') {
        localStorage.setItem(
          'aa_pwa_installed',
          '1'
        );
      } else {
        localStorage.setItem(
          'aa_install_dismissed',
          '1'
        );
      }

      onDismiss();
    } catch {
      onDismiss();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 76,
        right: 14,
        left: 14,
        zIndex: 200,
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 18,
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,.4)',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background:
            'linear-gradient(135deg,#0369a1,#0ea5e9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
        }}
      >
        📱
      </div>

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: 'var(--text)',
          }}
        >
          نصب روی گوشی
        </div>

        <div
          style={{
            fontSize: 11,
            color: 'var(--text2)',
          }}
        >
          دسترسی سریع‌تر و آفلاین
        </div>
      </div>

      <button
        onClick={installApp}
        style={{
          background:
            'linear-gradient(135deg,#0369a1,#0ea5e9)',
          color: 'white',
          border: 'none',
          padding: '8px 14px',
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        نصب
      </button>

      <button
        onClick={() => {
          localStorage.setItem(
            'aa_install_dismissed',
            '1'
          );

          onDismiss();
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text2)',
          fontSize: 18,
          cursor: 'pointer',
        }}
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Update Banner (Service Worker)
══════════════════════════════════════════════════ */

function UpdateBanner({ show }) {
  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 300,
        background:
          'linear-gradient(90deg,#059669,#10b981)',
        color: 'white',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <RefreshCw size={14} />
      نسخه جدید آماده است

      <button
        onClick={() => window.location.reload()}
        style={{
          background: 'rgba(255,255,255,.2)',
          border: '1px solid rgba(255,255,255,.3)',
          borderRadius: 8,
          padding: '4px 12px',
          color: 'white',
          cursor: 'pointer',
        }}
      >
        بروزرسانی
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Main App
══════════════════════════════════════════════════ */

export default function App() {
  // tab از URL خوانده می‌شود (?tab=nearest)
  const [tab, setTabRaw] = useState(() => {
    const t = getUrlParam('tab');
    return ['today', 'all', 'nearest', 'about'].includes(t) ? t : 'today';
  });

  // برچسب فعال از URL (?tag=شرق-تهران)
  const [tagSlug, setTagSlug] = useState(() => getUrlParam('tag'));

  // تغییر tab: state + URL، و پاک کردن برچسب
  const setTab = (t) => {
    setTabRaw(t);
    setTagSlug('');
    setUrlParams({ tab: t, tag: '' });
  };

  // پاک کردن برچسب و بازگشت به حالت عادی
  const clearTag = () => {
    setTagSlug('');
    setUrlParams({ tag: '' });
  };

  const [dark, setDark] = useState(() => {
    try {
      return localStorage.getItem('aa_theme') !== 'light';
    } catch {
      return true;
    }
  });

  const [swUpdate, setSwUpdate] = useState(false);

  const [installPrompt, setInstallPrompt] = useState(null);

  const [isInstalled, setIsInstalled] = useState(() => {
      try {
        return (
          localStorage.getItem('aa_pwa_installed') === '1'
        );
      } catch {
        return false;
      }
    });

  const { meetings, isOffline } = useMeetings({});

  const { alertedIds, syncToSW } = useNotifications();

  useAlarmSync(meetings, alertedIds, syncToSW);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('aa_theme', next ? 'dark' : 'light');
    applyTheme(next);
  };

  useEffect(() => {
    applyTheme(dark);
  }, [dark]);

  // همگام‌سازی با دکمه back/forward مرورگر
  useEffect(() => {
    const onPop = () => {
      const t = getUrlParam('tab');
      setTabRaw(['today', 'all', 'nearest', 'about'].includes(t) ? t : 'today');
      setTagSlug(getUrlParam('tag'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // منطق چک کردن ورژن دیتابیس با سرور در هنگام لود اپلیکیشن
  useEffect(() => {
    const checkCacheVersion = async () => {
      try {
        const response = await fetch(buildApiUrl({ action: 'stats' }));
        const data = await response.json();
        const serverVersion = data.cache_version;

        if (serverVersion) {
          const localVersion = localStorage.getItem('aa_cache_version');

          if (localVersion && localVersion !== serverVersion.toString()) {
            const keys = Object.keys(localStorage);
            keys.forEach(k => {
              if (k.startsWith('aa_') && k !== 'aa_theme' && k !== 'aa_pwa_installed' && k !== 'aa_install_dismissed') {
                 localStorage.removeItem(k);
              }
            });

            localStorage.setItem('aa_cache_version', serverVersion);
            window.location.reload();
          } else if (!localVersion) {
            localStorage.setItem('aa_cache_version', serverVersion);
          }
        }
      } catch (e) {
        console.error("بررسی همگام‌سازی کش ناموفق بود.", e);
      }
    };

    checkCacheVersion();
  }, []);

  useEffect(() => {
    const h = () => setSwUpdate(true);
    window.addEventListener('sw-update', h);

    return () =>
      window.removeEventListener('sw-update', h);
  }, []);

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (standalone) {
      setIsInstalled(true);
      localStorage.setItem('aa_pwa_installed', '1');
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();

      const dismissed = localStorage.getItem('aa_install_dismissed');
      const installed = localStorage.getItem('aa_pwa_installed');

      if (dismissed === '1' || installed === '1') {
        return;
      }

      setInstallPrompt(e);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      localStorage.setItem('aa_pwa_installed', '1');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    clearOldCaches();

    const swPath =
      window.location.pathname.startsWith('/meetings')
        ? '/meetings/service-worker.js'
        : '/service-worker.js';

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register(swPath)
        .catch(() => {});
    }
  }, []);

  return (
    <ThemeCtx.Provider
      value={{
        dark,
        toggle: toggleTheme,
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          direction: 'rtl',
        }}
      >
        <UpdateBanner show={swUpdate} />

        <div
          style={{
            maxWidth: 560,
            margin: '0 auto',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Header
            tab={tab}
            isOffline={isOffline}
            dark={dark}
            toggleTheme={toggleTheme}
            tagActive={!!tagSlug}
          />

          <main
            style={{
              flex: 1,
              padding: '16px 16px 90px',
            }}
          >
            {tagSlug ? (
              <TagPage slug={tagSlug} onClear={clearTag} />
            ) : (
              <>
                {tab === 'today' && <TodayPage />}

                {tab === 'all' && <AllMeetingsPage />}

                {tab === 'nearest' && (
                  <NearestPage />
                )}

                {tab === 'about' && <AboutPage />}
              </>
            )}
          </main>

          <BottomNav
            tab={tab}
            setTab={setTab}
          />
        </div>

        <NotificationSetup
          alertedCount={alertedIds?.length}
        />

        <InstallBanner
          prompt={
            !isInstalled
              ? installPrompt
              : null
          }
          onDismiss={() => {
            setInstallPrompt(null);
          }}
        />
      </div>
    </ThemeCtx.Provider>
  );
}
